import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.reducer';
import { unsetUser, loadUser } from 'src/app/auth/auth.actions';
import { SignupStateService } from '../../services/signup-state.service';
import {
  KycApiService,
  SendOtpCode,
  SendOtpResponse,
  VerifyOtpResponse,
} from 'src/app/auth/services/kyc-api.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import { OtpInputComponent } from '../shared/otp-input/otp-input.component';
import {
  COUNTRY_PHONE_OPTIONS,
  CountryPhone,
  getCountryByCode,
  detectCountryFromInput,
  normalizePhoneInput,
  phonePatternFor,
  phoneErrorFor,
  phoneHintFor,
  phoneValidatorFor,
  toE164,
  PASOS_COMUNES,
} from '../../constants/signup.constants';

@Component({
  selector: 'app-whatsapp-otp-step',
  templateUrl: './whatsapp-otp-step.component.html',
  styleUrls: ['./whatsapp-otp-step.component.sass'],
})
export class WhatsappOtpStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  @ViewChild('otpInput') otpInput!: OtpInputComponent;

  // Estado
  phoneCode: string = '';
  phone: string = '';
  loading: boolean = false;
  isExpired: boolean = false;
  canResend: boolean = false;
  showEditPhone: boolean = false;

  // Error de WhatsApp (número no tiene WhatsApp)
  whatsappError: boolean = false;
  whatsappErrorMessage: string = '';

  // Countdown — se deriva de los deadlines que dejó el KYC backend en el estado
  // del wizard (ver `signupState.applyWhatsappOtpResponse`).
  countdown: number = 0;
  // Segundos que faltan para poder pedir otro código (cooldown del backend).
  resendCountdown: number = 0;
  private countdownInterval: any;

  // Formulario para editar teléfono
  editPhoneForm!: FormGroup;
  editPhoneLoading: boolean = false;
  countryPhoneOptions = COUNTRY_PHONE_OPTIONS;
  selectedCountry: CountryPhone = COUNTRY_PHONE_OPTIONS[0];
  showCountryDropdown: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private cognitoService: CognitoService,
    public signupState: SignupStateService,
    private kycService: KycApiService,
    private companyRegistrationService: CompanyRegistrationService
  ) {}

  ngOnInit(): void {
    // Verificar que hay datos de formulario válidos
    const formData = this.signupState.currentState.formData;
    if (!formData?.phone || !formData?.phoneCode) {
      this.signupState.goToStep(PASOS_COMUNES.FORMULARIO);
      return;
    }

    // Verificar que el email ya fue verificado (paso anterior)
    if (!this.signupState.currentState.emailVerified) {
      this.signupState.goToStep(PASOS_COMUNES.OTP_EMAIL);
      return;
    }

    // Si el teléfono ya está verificado, avanzar automáticamente
    if (this.signupState.currentState.phoneVerified) {
      if (this.signupState.isEmpresa) {
        // Reintentar registro de empresa (pudo fallar en sesión anterior)
        this.registerCompanyAndContinue();
      } else {
        this.signupState.nextStep();
      }
      return;
    }

    this.phoneCode = formData.phoneCode;
    this.phone = formData.phone;

    // Establecer país seleccionado
    this.selectedCountry = getCountryByCode(this.phoneCode);

    this.initEditPhoneForm();
    this.checkInitialOtpStatus();
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  private initEditPhoneForm(): void {
    this.editPhoneForm = this.fb.group({
      // Guarda lo que escribe el usuario; el validador lo juzga normalizado.
      newPhone: [
        '',
        [Validators.required, phoneValidatorFor(() => this.selectedCountry)],
      ],
    });
  }

  // El ejemplo y el largo del campo salen del pais elegido (misma tabla que
  // usa el formulario de registro).
  get phonePlaceholder(): string {
    return this.selectedCountry.placeholder;
  }

  get phoneHint(): string {
    return phoneHintFor(this.selectedCountry);
  }

  // Error del campo, una vez que el usuario salio de el.
  get newPhoneError(): string {
    const control = this.editPhoneForm?.get('newPhone');
    if (!control?.touched || !control.errors?.['pattern']) return '';
    return phoneErrorFor(this.selectedCountry, control.value || '');
  }

  /**
   * Verifica si el OTP fue enviado correctamente al llegar a este paso
   */
  private async checkInitialOtpStatus(): Promise<void> {
    // Verificar si hubo error en el envío inicial (paso 3)
    const whatsappError = this.signupState.currentState.whatsappOtpError;
    if (whatsappError) {
      const code = this.signupState.currentState.whatsappOtpErrorCode;
      this.signupState.clearWhatsappOtpError();
      // Ningún rechazo reemplaza el código anterior: si quedó vencimiento
      // guardado, `handleSendError` levanta el countdown y el usuario puede
      // validar el código que ya tiene en el teléfono. Si no quedó, el paso
      // arranca con el reenvío disponible en vez de quedar muerto.
      this.handleSendError({ code, message: whatsappError });
      return;
    }

    // Restaurar countdown desde el vencimiento persistido
    this.applyOtpRemaining(this.secondsUntilOtpExpiry());
  }

  // ==================== COUNTDOWN ====================

  /**
   * Segundos que le quedan al OTP, según el deadline que dejó el backend. El
   * TTL lo decide el KYC backend y no se replica acá.
   */
  private secondsUntilOtpExpiry(): number {
    return this.secondsUntil(this.signupState.currentState.whatsappOtpExpiresAtMs);
  }

  /**
   * Segundos que faltan para poder reenviar (cooldown anti-ban del backend).
   * Es más corto que la vida del código: el botón se habilita antes de que el
   * código expire, y por eso no se cuelga de `isExpired`.
   */
  private secondsUntilResendAvailable(): number {
    return this.secondsUntil(this.signupState.currentState.whatsappOtpResendAtMs);
  }

  /** Segundos hasta un deadline en epoch ms; 0 si no hay deadline o ya pasó. */
  private secondsUntil(deadlineMs: number | null): number {
    if (!deadlineMs) {
      return 0;
    }
    const remaining = Math.floor((deadlineMs - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  }

  private startCountdown(): void {
    this.isExpired = false;
    this.whatsappError = false;
    this.startTicker();
  }

  /**
   * Un tick del reloj: countdown del código y cooldown de reenvío se
   * recalculan contra sus deadlines absolutos, no se decrementan. En móvil el
   * browser congela los intervalos de una pestaña en segundo plano, y un
   * contador que resta 1 por tick volvía mostrando más tiempo del real.
   *
   * El reenvío se habilita cuando vence el cooldown del backend, no cuando
   * expira el código. Un código marcado como inutilizable (`isExpired`) no
   * revive aunque su deadline siga en el futuro.
   */
  private tick(): void {
    if (!this.isExpired) {
      this.countdown = this.secondsUntilOtpExpiry();
      if (this.countdown <= 0) {
        this.isExpired = true;
      }
    }
    this.resendCountdown = this.secondsUntilResendAvailable();
    this.canResend = this.resendCountdown <= 0;

    // Sin código que contar ni cooldown que esperar, no hay nada que mirar.
    if (this.isExpired && this.canResend) {
      this.stopCountdown();
    }
  }

  private startTicker(): void {
    this.stopCountdown();
    this.tick();
    if (!this.isExpired || !this.canResend) {
      this.countdownInterval = setInterval(() => this.tick(), 1000);
    }
  }

  /**
   * Arranca el countdown con los segundos restantes, o marca el código como
   * vencido si ya no queda nada (reloj del cliente corrido, el envío tardó
   * más que el TTL, o directamente no hay código). En ese caso el reenvío
   * queda disponible apenas lo permita el cooldown, para que el paso no quede
   * con "expira en 00:00" y todo deshabilitado.
   */
  private applyOtpRemaining(remaining: number): void {
    if (remaining <= 0) {
      this.countdown = 0;
      this.isExpired = true;
      this.startTicker();
      return;
    }
    this.startCountdown();
  }

  /**
   * El backend dijo que el código ya no sirve (vencido, bloqueado por
   * intentos o inexistente). Se olvida su vencimiento, también en el estado
   * persistido, para que el usuario pida otro en vez de seguir tipeando.
   */
  private marcarCodigoInutilizable(): void {
    this.signupState.invalidateWhatsappOtpCode();
    this.applyOtpRemaining(0);
  }

  /**
   * Persiste los deadlines que devolvió el backend y reinicia el countdown con
   * ellos. Usar siempre esto después de un envío/reenvío exitoso.
   */
  private restartCountdownFrom(response: SendOtpResponse): void {
    this.signupState.applyWhatsappOtpResponse(response);
    this.applyOtpRemaining(this.secondsUntilOtpExpiry());
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  get countdownFormatted(): string {
    return this.formatMinutesSeconds(this.countdown);
  }

  get resendCountdownFormatted(): string {
    return this.formatMinutesSeconds(this.resendCountdown);
  }

  private formatMinutesSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  get fullPhoneNumber(): string {
    return toE164(this.phoneCode, this.phone);
  }

  get displayPhone(): string {
    return `${this.phoneCode} ${this.phone}`;
  }

  // ==================== CONFIRMAR OTP ====================

  async onCodeComplete(code: string): Promise<void> {
    if (this.loading) return;
    await this.confirmWhatsappOtp(code);
  }

  async confirmWhatsappOtp(code: string): Promise<void> {
    if (!code || code.length !== 6) return;

    // Bloquear validación si el código expiró en el frontend
    if (this.isExpired) {
      this.onError.emit('El código ha expirado. Solicita uno nuevo.');
      this.otpInput?.triggerError();
      this.otpInput?.reset();
      return;
    }

    this.loading = true;

    try {
      const response = await this.kycService.verifyOtp(this.fullPhoneNumber, code);

      if (!response.success) {
        this.handleVerifyError(response);
        this.otpInput?.triggerError();
        this.otpInput?.reset();
        this.loading = false;
        return;
      }

      // Éxito - marcar teléfono verificado
      this.signupState.setPhoneVerified(true);
      this.onSuccess.emit('Número de WhatsApp verificado correctamente');

      // Flujo condicional por tipo de cuenta
      if (this.signupState.isEmpresa) {
        await this.registerCompanyAndContinue();
      } else {
        // Persona - ir al paso 5 (OCR)
        await this.signupState.nextStep();
      }

    } catch (error: any) {
      this.onError.emit('Error al verificar el código. Intenta nuevamente.');
      this.otpInput?.triggerError();
      this.otpInput?.reset();
    } finally {
      this.loading = false;
    }
  }

  /**
   * Maneja errores de verificación OTP según el `code` del backend KYC (el
   * `message` es solo para mostrar). EXPIRED, BLOCKED y NO_PENDING dejan el
   * código inutilizable: se marca así para que el usuario pida otro en vez de
   * seguir tipeando contra un código que el backend ya no acepta.
   */
  private handleVerifyError(response: VerifyOtpResponse): void {
    switch (response.code) {
      case 'EXPIRED':
        this.marcarCodigoInutilizable();
        this.onError.emit('El código ha expirado. Solicita uno nuevo.');
        return;

      case 'BLOCKED':
        this.marcarCodigoInutilizable();
        this.onError.emit('Demasiados intentos fallidos. Solicita un nuevo código.');
        return;

      case 'NO_PENDING':
        this.marcarCodigoInutilizable();
        this.onError.emit('No hay código pendiente. Solicita uno nuevo.');
        return;

      case 'INVALID_CODE':
        this.onError.emit(
          response.remainingAttempts !== undefined
            ? `Código incorrecto. Intentos restantes: ${response.remainingAttempts}`
            : response.message || 'Código incorrecto.'
        );
        return;

      default:
        // ERROR (interno o de red): el código sigue valiendo, se reintenta.
        this.onError.emit(response.message || 'Error al verificar el código. Intenta nuevamente.');
    }
  }

  /**
   * Crea el registro vacío de la empresa y sigue según el flag del flujo.
   *
   * Con `empresaSignupV2Enabled` encendido el usuario NO sale del registro: el
   * estado del signup se mantiene y el paso 5 (pantalla informativa) se muestra
   * a continuación. El flag 'aguacate_registration_in_progress' sigue puesto,
   * así que el guard impide entrar al dashboard con el registro a medias.
   *
   * Apagado, se cierra el registro y se sale al dashboard como antes de este
   * refactor: el ActivateGuard manda la empresa al monolito
   * `company-registration`, que reanuda desde el mismo estado.
   */
  private async registerCompanyAndContinue(): Promise<void> {
    try {
      const formData = this.signupState.currentState.formData;
      const cognitoId = this.signupState.currentState.cognitoId;

      if (!formData || !cognitoId) {
        throw new Error('Datos de registro incompletos');
      }

      // Registrar empresa en backend
      await this.companyRegistrationService.create({
        email: formData.email,
        telefono: formData.phone,
        prefijoTelefono: formData.phoneCode,
        cognitoId: cognitoId,
      });

      // Limpiar store de usuario anterior (si existía)
      this.store.dispatch(unsetUser());

      // Obtener el rol del usuario recién registrado desde Cognito
      const cognitoSession = await this.cognitoService.getSession();
      const userGroup = cognitoSession?.['accessToken']?.['payload']?.['cognito:groups']?.[0] || '';

      // Cargar el usuario nuevo en el store. Además del rol, esto trae el
      // usuarioId numérico que necesitan los pasos siguientes de empresa.
      this.store.dispatch(loadUser({ email: formData.email, role: userGroup }));

      if (!this.signupState.empresaFlujoNuevo) {
        // Flujo anterior: cerrar el registro y salir al dashboard.
        this.signupState.completeRegistration();
        this.router.navigate(['/wallet']);
        return;
      }

      // Continuar al paso 5 (pantalla informativa)
      await this.signupState.nextStep();

    } catch (error: any) {
      this.onError.emit(error?.message || 'Error al registrar la empresa. Intenta nuevamente.');
    }
  }

  // ==================== REENVIAR CÓDIGO ====================

  async resendCode(): Promise<void> {
    if (!this.canResend || this.loading) return;

    this.loading = true;

    try {
      const response = await this.kycService.resendOtp(this.fullPhoneNumber);

      if (!response.success) {
        // Ningún rechazo toca el código vigente, y el backend manda sus
        // tiempos. Guardarlos deja el countdown y el botón de reenviar
        // alineados con el backend en vez de permitir reintentos que van a
        // fallar igual.
        this.signupState.applyWhatsappOtpRejection(response);
        this.handleSendError(response);
        this.loading = false;
        return;
      }

      this.onSuccess.emit('Código reenviado correctamente');
      this.restartCountdownFrom(response);
      this.otpInput?.reset();

    } catch (error: any) {
      this.onError.emit('No se pudo reenviar el código. Intenta más tarde.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Maneja un rechazo de envío al número actual según el `code` del backend
   * KYC. Los deadlines del rechazo ya tienen que estar aplicados al estado.
   *
   * Solo NOT_ON_WHATSAPP es culpa del número y pide cambiarlo. El resto
   * (COOLDOWN, RATE_LIMITED, UNAVAILABLE, SEND_FAILED, ERROR) no: se muestra
   * el mensaje, el código vigente (si hay) sigue contando y el reenvío queda
   * disponible cuando lo diga el cooldown. Mandar a editar el número por un
   * WhatsApp caído o por carga general hacía cambiar un número que estaba bien.
   */
  private handleSendError(rechazo: { code?: SendOtpCode | null; message?: string }): void {
    if (rechazo.code === 'NOT_ON_WHATSAPP') {
      this.whatsappError = true;
      this.whatsappErrorMessage = this.mensajeDeRechazoEnvio(rechazo);
      this.stopCountdown();
      this.onError.emit(this.whatsappErrorMessage);
      return;
    }

    this.onError.emit(this.mensajeDeRechazoEnvio(rechazo));
    this.applyOtpRemaining(this.secondsUntilOtpExpiry());
  }

  private mensajeDeRechazoEnvio(rechazo: { code?: SendOtpCode | null; message?: string }): string {
    if (rechazo.code === 'NOT_ON_WHATSAPP') {
      return 'Este número no está registrado en WhatsApp. Por favor ingresa un número con WhatsApp activo.';
    }
    return rechazo.message || 'Error al enviar el código. Intenta nuevamente.';
  }

  // ==================== EDITAR TELÉFONO ====================

  toggleEditPhone(): void {
    this.showEditPhone = !this.showEditPhone;
    this.showCountryDropdown = false;
    if (this.showEditPhone) {
      this.editPhoneForm.reset();
      // Pre-seleccionar el país actual
      this.setEditCountry(getCountryByCode(this.phoneCode));
    }
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
  }

  selectCountry(country: CountryPhone): void {
    this.setEditCountry(country);
    this.showCountryDropdown = false;
  }

  /**
   * Cambia el pais del formulario de edicion y re-valida lo ya escrito con
   * las reglas del pais nuevo. No se recorta nada: si no calza, se marca.
   */
  private setEditCountry(country: CountryPhone): void {
    this.selectedCountry = country;
    this.editPhoneForm?.get('newPhone')?.updateValueAndValidity();
  }

  async saveNewPhone(): Promise<void> {
    this.commitNewPhone();
    if (!this.editPhoneForm.valid) return;

    const newPhone = this.editPhoneForm.get('newPhone')?.value;
    const newPhoneCode = this.selectedCountry.code;
    const newFullPhone = toE164(newPhoneCode, newPhone);

    if (newFullPhone === this.fullPhoneNumber) {
      this.onError.emit('El nuevo número es igual al actual.');
      return;
    }

    this.editPhoneLoading = true;

    try {
      // Enviar OTP al nuevo número
      const response = await this.kycService.sendOtp(newFullPhone);

      if (!response.success) {
        // El número no cambia hasta que el nuevo recibe su código, así que no
        // se toca el countdown del anterior: si su código sigue vigente, el
        // usuario puede cancelar y usarlo. El formulario queda abierto para
        // corregir el número (NOT_ON_WHATSAPP) o reintentar (el resto). Los
        // deadlines del rechazo tampoco se guardan: son del número nuevo.
        this.onError.emit(this.mensajeDeRechazoEnvio(response));
        this.editPhoneLoading = false;
        return;
      }

      // Actualizar estado con nuevo número
      const formData = this.signupState.currentState.formData;
      this.signupState.setFormData({
        ...formData!,
        phoneCode: newPhoneCode,
        phone: newPhone,
      });

      this.phoneCode = newPhoneCode;
      this.phone = newPhone;
      this.showEditPhone = false;
      this.whatsappError = false;
      this.onSuccess.emit('Código enviado al nuevo número');
      this.restartCountdownFrom(response);
      this.otpInput?.reset();

    } catch (error: any) {
      this.onError.emit('Error al cambiar el número. Intenta nuevamente.');
    } finally {
      this.editPhoneLoading = false;
    }
  }

  cancelEditPhone(): void {
    this.showEditPhone = false;
    this.showCountryDropdown = false;
    this.editPhoneForm.reset();
  }

  // Mismo criterio que el formulario de registro: el número se escribe o
  // pega como lo tenga la persona, sin teclas bloqueadas ni recortes, y se
  // limpia al salir del campo y al enviar.

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const allowed = input.value.replace(/[^\d+\s().-]/g, '');
    if (allowed !== input.value) {
      this.editPhoneForm.get('newPhone')?.setValue(allowed);
    }
    this.followCountryOf(allowed);
  }

  // Si lo pegado es un número completo, reemplaza lo que hubiera.
  onPastePhone(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    this.followCountryOf(pasted);
    const digits = normalizePhoneInput(pasted, this.selectedCountry);
    if (phonePatternFor(this.selectedCountry).test(digits)) {
      event.preventDefault();
      const control = this.editPhoneForm.get('newPhone');
      control?.setValue(digits);
      control?.markAsTouched();
    }
  }

  onPhoneBlur(): void {
    this.commitNewPhone();
  }

  private followCountryOf(raw: string): void {
    const detected = detectCountryFromInput(raw);
    if (detected && detected !== this.selectedCountry) {
      this.setEditCountry(detected);
    }
  }

  private commitNewPhone(): void {
    const control = this.editPhoneForm?.get('newPhone');
    const raw: string = control?.value || '';
    this.followCountryOf(raw);
    const digits = normalizePhoneInput(raw, this.selectedCountry);
    if (digits !== raw && phonePatternFor(this.selectedCountry).test(digits)) {
      control?.setValue(digits);
    }
  }

  // ==================== NAVEGACIÓN ====================

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }

  trackByCode(index: number, country: CountryPhone): string {
    return country.code;
  }
}
